import USER_TYPES from '@/constants/authorization/user-types'
import RolesApi from '@/API/users/roles-api'
import { normalizeSubuserPermissions } from '@/utils/subuser-permissions'
import { matchesSubuserPermission, normalizePermissionCatalogResponse } from '@/constants/authorization/subuser-permission-slugs'

export default {
  namespaced: true,
  state: {
    adminPermissions: {}, // super-admin permissions
    primaryAccount: false,
    userType: null,
    admin_level: 2,
    inventoryPermissions: {},
    permissions: {},
    permissionsFetched: false,
    permissionsLoading: false,
    roles: [],
    rolesFetched: false,
    rolesForSubusers: [],
    rolesForSubusersFetched: false,
    subuserRoles: [] as string[],
    subuserPermissions: [] as string[],
  },
  getters: {
    isSuperAdmin: (state: any) => state.admin_level === 0 && state.primaryAccount,
    isManager: (state: any) => state.admin_level === 3 && state.primaryAccount,
    isPrimaryAccount: (state: any) => state.primaryAccount,
    isSubUser: (state: any) => !state.primaryAccount,
    isBranchAdmin: (state: any) => state.userType === USER_TYPES.BRANCH_ADMIN,
    isChef: (state: any) => state.userType === USER_TYPES.KITCHEN_RUNNER,
    isSupervisor: (state: any) => state.userType === USER_TYPES.KITCHEN_SUPERVISOR,
    isWaiter: (state: any) => state.userType === USER_TYPES.KITCHEN_WAITER,
    isAuditor: (state: any) => state.userType === USER_TYPES.AUDITOR,
    isDriver: (state: any) => state.userType === USER_TYPES.Driver,
    isKitchenUser: (state: any, getters: any) => getters.isChef || getters.isSupervisor || getters.isWaiter,
    isUserHasType: (state: any, getters: any) => (type: number, strict = false) => {
      switch (type) {
        case USER_TYPES.SUPER_ADMIN: return getters.isSuperAdmin
        case USER_TYPES.ACCOUNT_MANAGER: return getters.isManager || (!strict && getters.isSuperAdmin)
        case USER_TYPES.OWNER: return getters.isPrimaryAccount || (!strict && getters.isSuperAdmin)
        case USER_TYPES.BRANCH_ADMIN: return getters.isBranchAdmin || (!strict && getters.isPrimaryAccount)
        case USER_TYPES.KITCHEN_USER: return getters.isKitchenUser || (!strict && getters.isPrimaryAccount)
        case USER_TYPES.Driver: return getters.isDriver || (!strict && getters.isPrimaryAccount)
        default: return state.userType === type || (!strict && getters.isPrimaryAccount)
      }
    },
    isSuperAdminHasPermission: (state: any) => (permission: string) => state.adminPermissions?.[permission] ?? false,
    inventoryPermissions: (state: any) => (key: string) => state.inventoryPermissions?.[key] ?? false,
    permissions: (state: any) => state.permissions ?? {},
    permissionCatalog: (state: any) => normalizePermissionCatalogResponse(state.permissions),
    permissionsLoading: (state: any) => state.permissionsLoading,
    permissionsFetched: (state: any) => state.permissionsFetched,
    roles: (state: any) => state.roles ?? [],
    rolesForSubusers: (state: any) => state.rolesForSubusers ?? [],
    subuserRoles: (state: any) => state.subuserRoles ?? [],
    subuserPermissions: (state: any) => state.subuserPermissions ?? [],
    hasSubuserPermission: (state: any, getters: any) => (permission: string) => {
      if (getters.isPrimaryAccount) return true
      if (!permission) return false
      return matchesSubuserPermission(state.subuserPermissions, permission)
    },
    hasAnySubuserPermission: (state: any, getters: any) => (permissions: string[] = []) => {
      if (getters.isPrimaryAccount) return true
      if (!permissions.length) return true
      return permissions.some(permission => matchesSubuserPermission(state.subuserPermissions, permission))
    },
    hasAllSubuserPermissions: (state: any, getters: any) => (permissions: string[] = []) => {
      if (getters.isPrimaryAccount) return true
      if (!permissions.length) return true
      return permissions.every(permission => matchesSubuserPermission(state.subuserPermissions, permission))
    },
    hasSubuserRole: (state: any, getters: any) => (roleName: string) => {
      if (getters.isPrimaryAccount) return true
      if (!roleName) return false
      return state.subuserRoles.includes(roleName)
    },
  },
  mutations: {
    SET_PERMISSIONS(state: any, { admin_permissions, user }: any) {
      state.primaryAccount = user.primaryAccount
      state.adminPermissions = admin_permissions ?? []
      state.userType = user.type
      state.admin_level = user.admin_level
    },
    inventoryPermissions(state: any, permissions: any) {
      state.inventoryPermissions = permissions
    },
    SET_ROLES_PERMISSIONS(state: any, permissions: any) {
      state.permissionsFetched = true
      state.permissionsLoading = false
      state.permissions = normalizePermissionCatalogResponse(permissions)
    },
    SET_ROLES_PERMISSIONS_LOADING(state: any, loading: boolean) {
      state.permissionsLoading = loading
    },
    SET_ROLES(state: any, roles: any) {
      state.rolesFetched = true
      state.roles = roles ?? []
    },
    SET_ROLES_FOR_SUBUSERS(state: any, roles: any) {
      state.rolesForSubusersFetched = true
      state.rolesForSubusers = roles ?? []
    },
    SET_SUBUSER_PERMISSIONS(state: any, payload: any) {
      const normalized = normalizeSubuserPermissions(payload)
      state.subuserRoles = normalized.roles
      state.subuserPermissions = normalized.permissions
    },
    RESET_SUBUSER_PERMISSIONS(state: any) {
      state.subuserRoles = []
      state.subuserPermissions = []
    },
  },
  actions: {
    async FETCH_ROLES_PERMISSIONS(context: any, force = false) {
      if (!force && context.state.permissionsFetched) return
      context.commit('SET_ROLES_PERMISSIONS_LOADING', true)
      RolesApi.getPermissions()
        .then(res => {
          context.commit('SET_ROLES_PERMISSIONS', res)
        })
        .catch(() => {
          context.commit('SET_ROLES_PERMISSIONS_LOADING', false)
        })
    },
    async FETCH_ROLES(context: any, force = false) {
      if (!force && context.state.rolesFetched) return
      RolesApi.getRoles({ page: 1, per_page: 500 }).then(res => {
        const roles = res?.roles ?? (Array.isArray(res) ? res : [])
        context.commit('SET_ROLES', roles)
      }).catch(() => {})
    },
    async FETCH_ROLES_FOR_SUBUSERS(context: any, force = false) {
      if (!force && context.state.rolesForSubusersFetched) return
      RolesApi.getRolesForSubusers({ page: 1, per_page: 500 }).then(res => {
        const roles = res?.roles ?? (Array.isArray(res) ? res : [])
        context.commit('SET_ROLES_FOR_SUBUSERS', roles)
      }).catch(() => {})
    },
  },
}
